import cn from 'classnames'
import Link, { LinkProps } from 'next/link'
import React, { ReactNode } from 'react'
import { isSpecialKeyPressed } from '../src/utils/isSpecialKeyPressed'
import { ConditionalWrap } from './ConditionalWrap'

export interface CollapsibleProps {
	label: string | ReactNode
	content: string | ReactNode
	large?: boolean
	isPrintable?: boolean
	linkProps?: LinkProps
}

export const Collapsible: React.FunctionComponent<CollapsibleProps> = ({
	label,
	content,
	large,
	isPrintable,
	linkProps,
}) => {
	const [isExpanded, setIsExpanded] = React.useState(false)
	const [isTransitioning, setIsTransitioning] = React.useState(false)
	const toggle = React.useCallback(
		(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (event.currentTarget.tagName === 'A' && isSpecialKeyPressed(event)) {
				return
			}
			event.preventDefault()
			setIsTransitioning(true)
			setIsExpanded(!isExpanded)
		},
		[isExpanded],
	)

	const hasLink = Boolean(linkProps)

	return (
		<div
			className={cn(
				'collapsible',
				isExpanded && 'is-expanded',
				isTransitioning && 'is-transitioning',
				large && 'view-large',
				isPrintable === undefined ? ( isExpanded && 'is-printable') : (isPrintable && 'is-printable')
			)}
		>
			<ConditionalWrap
				condition={hasLink}
				wrap={(children) => (
					<Link {...linkProps!}>
						<a className="collapsible-label" onClick={toggle}>
							{children}
						</a>
					</Link>
				)}
			>
				<ConditionalWrap
					condition={!hasLink}
					wrap={(children) => (
						<button
							className="collapsible-label"
							type="button"
							onClick={toggle}
						>
							{children}
						</button>
					)}
				>
					<>{label}</>
				</ConditionalWrap>
			</ConditionalWrap>

			<div
				className="collapsible-content"
				onTransitionEnd={() => setIsTransitioning(false)}
			>
				<div className="collapsible-content-in">{content}</div>
			</div>
		</div>
	)
}
